import type { AtomData, GenericPermission } from "@atoms/atom-types";
import { createContext, type ReactNode } from "react";

export type LabelType<T> = Record<
  keyof T,
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  { label: string; renderer?: (value: any) => ReactNode }
>;

export const MagicFormContext = createContext<FormContextType | undefined>(
  undefined,
);
export const MagicFormProvider = MagicFormContext.Provider;

export type ChangeSet<AtomDataType extends AtomData = AtomData> =
  Partial<AtomDataType>;

export interface SuggestedChange<AtomDataType extends AtomData = AtomData> {
  source?: string;
  explanation?: string;
  changeSet: ChangeSet<AtomDataType>;
}

export interface FormContextType<AtomDataType extends AtomData = AtomData> {
  currentValue: AtomDataType;
  updateDraft: (draft: Partial<AtomDataType>) => void;
  setDraft: (draft: AtomDataType) => void;
  checkFieldPermission: (
    field: string,
    permission: GenericPermission,
  ) => boolean;
  submit: (ignoreSuggestedChanges?: boolean) => Promise<void>;
  loading: boolean;
  draft: Partial<AtomDataType> | undefined;
  valueFromBackend: AtomDataType | undefined;
  resetDraft: () => Promise<void>;
  myUserId: string | undefined;
  suggestedChanges: SuggestedChange<AtomDataType> | undefined;
  setSuggestedChanges: (
    changes: SuggestedChange<AtomDataType> | undefined,
  ) => void;
  atomType: string;
  resolveSuggestedChange: (
    field: keyof AtomDataType,
    value: AtomDataType[keyof AtomDataType],
  ) => void;
  discardSuggestedChanges: () => void;
  onCancel: () => void | Promise<void>;
  labels?: LabelType<AtomDataType>;
  isDeleted: boolean;
  registerPreSubmit: (
    id: string,
    callback: () => Promise<Partial<AtomDataType> | undefined>,
    rollback?: () => Promise<void>,
  ) => void;
  unregisterPreSubmit: (id: string) => void;
  registerPostSubmit: (id: string, callback: () => Promise<void>) => void;
  unregisterPostSubmit: (id: string) => void;
}
